// ════════════════════════════════════════════════════════════
//  Ringside.jsx — THE HOURLY'S RINGSIDE: the wait before the bell
// ════════════════════════════════════════════════════════════
//  caP'n, 2026-10-16: "When watching tournaments, so I placed a bet and
//  there was still like a minute to go for the countdown, it'd be really
//  cool to see the combatants there with a crowd … Fireworks celebrations
//  etc eventually entrances xD definitely need Willie in the middle of the
//  ring doing the 'in this corner' type stuff :3 eventually can turn
//  off/on commentary"
//
//  The same shard the fight is fought on (Arena.jsx, untouched), ringed
//  by broken stands full of canon beings. Both trios wait in their
//  corners; Willie works the middle; fireworks go up for the last count.
//  ⚑ A SCAFFOLD, built to prove the seams (caP'n's big-build rule): the
//  stands, the cheer, the "entrance" pop and Willie's moves are all
//  placeholder-grade and expected to be replaced.
//
//  THE SEAMS
//  · `director` ref (written by RingsideScreen at 4 Hz): { leftMs, names,
//    teams, final } — the screen owns time; this scene only reads it.
//  · `cue` ref (written HERE every frame by the Director): { beat, look,
//    point, hype, cheer, corner, line } — Willie, the corners, the crowd,
//    the camera and the fireworks all read the same cue, so they can never
//    disagree. The DOM caption reads cue.line.
//  · ENTRANCES slot in at <Corner>: today a called corner "pops" (a bounce
//    and a ring of its colour). A real entrance replaces `entranceT` only.
//
//  Laws kept: no lights added past Arena's own · seeded, never random
//  (the stands and the fireworks pick from a PRNG) · one useFrame per
//  system · the being kits are vendored and never edited here.
// ════════════════════════════════════════════════════════════
import { memo, useLayoutEffect, useMemo, useRef } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import Arena, { Lights, FIELD_R } from "./Arena.jsx";
import WillieAnnouncer from "./WillieAnnouncer.jsx";
import { glowTex } from "./fxTex.js";
import { IEBeing, mulberry32 } from "../ieBeingModels.jsx";
import { TerrorSpecies } from "../ieTerrorSpecies.jsx";
import { STAND_HEIGHT } from "./Fighter.jsx";
import { ISLANDS, islandBob } from "./StadiumCrowd.jsx";
import Stands from "./Stands.jsx";
import SkyBox from "./SkyBox.jsx";
import { SkyOnlyCamera } from "./BattleCamera.jsx";
import { SKY_ANGLE } from "../data/skybox.js";
import {
  WORLD_BOUNDS, TETHERS, CORNERS, CORNER_SPOTS,
  FINAL_COUNT, BEAT, SCRIPT, finalLine, HANDOFF_LINE, CORNER_NAMES,
} from "../data/ringside.js";

export const SIDE_COLOR = ["#7CE7FF", "#FF4D8D"];   // the board's own: Khao blue / Anima pink
const ADD = THREE.AdditiveBlending;

// ── THE DIRECTOR — turns "how long is left" into one shared cue ──
function Director({ director, cue }) {
  useFrame(({ clock }) => {
    const d = director.current || {}, c = cue.current, t = clock.elapsedTime;
    const left = d.leftMs == null ? null : d.leftMs / 1000;
    const names = d.names || CORNER_NAMES;
    if (left != null && left <= 0.6) {
      Object.assign(c, { beat: "go", look: 0, point: false, hype: true, cheer: 1, corner: null, line: HANDOFF_LINE, final: true });
      return;
    }
    if (left != null && left <= FINAL_COUNT) {
      const n = Math.ceil(left);
      Object.assign(c, { beat: "final", look: 0, point: n <= 3, hype: true, cheer: 0.9, corner: null,
        line: finalLine(n), final: true });
      return;
    }
    const i = Math.floor(t / BEAT) % SCRIPT.length;
    const s = SCRIPT[i];
    const into = (t % BEAT) / BEAT;            // 0 → 1 through this beat
    c.beat = s.cue; c.final = false;
    c.line = s.line(names);
    if (s.cue === "A" || s.cue === "B") {
      const x = CORNERS[s.cue];
      c.look = Math.atan2(x, 0);               // turn to face the corner
      c.point = into > 0.08 && into < 0.85;
      c.hype = false; c.cheer = 0.75; c.corner = s.cue;
    } else if (s.cue === "welcome") {
      c.look = 0; c.point = false; c.hype = into < 0.6; c.cheer = 0.6; c.corner = null;
    } else {
      c.look = 0; c.point = false; c.hype = false; c.cheer = 0.15; c.corner = null;
    }
  });
  return null;
}

// ── a body, measured once and fitted to a height (same trick as Fighter) ──
function Fitted({ kit, id, tier, height }) {
  const fit = useRef(), inner = useRef();
  useLayoutEffect(() => {
    if (!fit.current || !inner.current) return;
    fit.current.scale.setScalar(1);
    inner.current.updateWorldMatrix(true, true);
    const box = new THREE.Box3().setFromObject(inner.current);
    const h = Math.max(0.3, box.max.y - Math.max(box.min.y, -0.3));
    fit.current.scale.setScalar(THREE.MathUtils.clamp(height / h, 0.35, 3));
  }, [kit, id, tier, height]);
  return (
    <group ref={fit}>
      <group ref={inner}>
        {kit === "species" ? <TerrorSpecies being={id} tier={tier} /> : <IEBeing being={id} tier={tier} />}
      </group>
    </group>
  );
}

// ── TETHERS — threads of Lyf light from the plate's rim to each lower island ──
// (Claude's invention, not canon — data/ringside.js TETHERS turns them off)
function Tethers() {
  const lower = useMemo(() => ISLANDS.map((isle, i) => ({ isle, i })).filter(({ isle }) => isle.di === 0), []);
  const refs = useRef([]);
  const mat = useMemo(() => new THREE.MeshBasicMaterial({ color: "#7FE8D8", transparent: true, opacity: 0.3, depthWrite: false, blending: ADD, toneMapped: false }), []);
  const geo = useMemo(() => new THREE.CylinderGeometry(0.05, 0.05, 1, 6, 1, true), []);
  const up = useMemo(() => new THREE.Vector3(0, 1, 0), []);
  const tmp = useMemo(() => ({ a: new THREE.Vector3(), b: new THREE.Vector3(), d: new THREE.Vector3() }), []);
  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    lower.forEach(({ isle, i }, k) => {
      const m = refs.current[k]; if (!m) return;
      const d = isle.deck, ang = isle.phi0 + isle.len / 2;
      const rFoot = FIELD_R - 0.4, rTop = d.r0 + 0.2;
      tmp.a.set(Math.sin(ang) * rFoot, -0.35, Math.cos(ang) * rFoot);                       // under the plate's rim
      tmp.b.set(Math.sin(ang) * rTop, d.y0 - 1.0 + islandBob(i, t), Math.cos(ang) * rTop);  // the island's front face
      tmp.d.subVectors(tmp.b, tmp.a);
      const len = tmp.d.length();
      m.position.copy(tmp.a).addScaledVector(tmp.d, 0.5);
      m.quaternion.setFromUnitVectors(up, tmp.d.normalize());
      m.scale.set(1, len, 1);
    });
    mat.opacity = 0.28 + Math.sin(t * 1.3) * 0.1;
  });
  return (
    <group>
      {lower.map((_, k) => <mesh key={k} ref={(el) => (refs.current[k] = el)} geometry={geo} material={mat} />)}
    </group>
  );
}

// ── A CORNER — the trio, waiting; it pops when Willie calls it ──
function Corner({ which, side, team, cue }) {
  const x = CORNERS[which], sx = Math.sign(x);
  const g = useRef(), ring = useRef();
  const st = useRef({ entranceT: 9, was: false });
  const glow = useMemo(() => glowTex(), []);
  useFrame(({ clock }, dtRaw) => {
    const dt = Math.min(dtRaw || 0.016, 0.05), c = cue.current, s = st.current, t = clock.elapsedTime;
    const called = c.corner === which;
    if (called && !s.was) s.entranceT = 0;   // ⚑ ENTRANCE SEAM: a real entrance replaces this pop
    s.was = called;
    s.entranceT += dt;
    const e = s.entranceT, pop = e < 0.6 ? Math.sin((e / 0.6) * Math.PI) * 0.18 : 0;
    if (g.current) g.current.children.forEach((k, i) => {
      if (!k.userData.body) return;
      const lag = Math.max(0, e - i * 0.12);
      const p = lag < 0.6 ? Math.sin((lag / 0.6) * Math.PI) * 0.18 : 0;
      k.scale.setScalar(1 + p);
      k.position.y = p * 1.2 + Math.sin(t * 1.6 + i) * 0.015;
    });
    if (ring.current) ring.current.children.forEach((m) => {
      m.material.opacity = 0.25 + (called ? 0.45 : 0) + pop;
      m.scale.setScalar(1 + pop * 0.6);
    });
  });
  if (!team || !team.length) return null;
  // every one of the three faces a point just in front of Willie
  const spots = CORNER_SPOTS.map(([ax, z]) => ({ x: sx * ax, z, yaw: Math.atan2(-sx * ax, 2.5 - z) }));
  return (
    <group>
      <group ref={ring}>
        {spots.map((p, i) => (
          <mesh key={i} rotation={[-Math.PI / 2, 0, 0]} position={[p.x, 0.03, p.z]}>
            <ringGeometry args={[0.62, 0.86, 40]} />
            <meshBasicMaterial color={SIDE_COLOR[side]} transparent opacity={0.3} blending={ADD} depthWrite={false} toneMapped={false} />
          </mesh>
        ))}
      </group>
      <sprite position={[x, 0.2, -0.5]} scale={[6, 1.2, 1]}>
        <spriteMaterial map={glow} color={SIDE_COLOR[side]} transparent opacity={0.22} blending={ADD} depthWrite={false} toneMapped={false} />
      </sprite>
      <group ref={g}>
        {team.slice(0, 3).map((line, i) => (
          <group key={line + i} userData={{ body: true }} position={[spots[i].x, 0, spots[i].z]} rotation={[0, spots[i].yaw, 0]}>
            <Fitted kit="lineage" id={line} tier="beb" height={STAND_HEIGHT.beb} />
          </group>
        ))}
      </group>
    </group>
  );
}

// ── WILLIE'S SPOT — a little dais in the middle, his colour ──
function Dais({ cue }) {
  return (
    <group>
      <mesh position={[0, 0.09, 0]}>
        <cylinderGeometry args={[0.95, 1.1, 0.18, 40]} />
        <meshStandardMaterial color="#2a2238" roughness={0.6} emissive="#3a2060" emissiveIntensity={0.4} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.185, 0]}>
        <ringGeometry args={[0.82, 0.92, 48]} />
        <meshBasicMaterial color="#F2C14E" transparent opacity={0.8} toneMapped={false} />
      </mesh>
      <group position={[0, 0.18, 0]}><WillieAnnouncer cue={cue} h={1.9} /></group>
    </group>
  );
}

// ── FIREWORKS — one pool, one useFrame ───────────────────────
const FW_MAX = 4000;
const FW_COLORS = ["#F2C14E", "#7CE7FF", "#FF4D8D", "#B388FF", "#7FE8D8", "#FFE9A0"].map((h) => new THREE.Color(h));
function Fireworks({ cue, on }) {
  const pts = useRef();
  const sim = useMemo(() => ({
    pos: new Float32Array(FW_MAX * 3), vel: new Float32Array(FW_MAX * 3), col: new Float32Array(FW_MAX * 3),
    life: new Float32Array(FW_MAX), next: 0, R: mulberry32(77), shells: [], timer: 1.5, lastBeat: "",
  }), []);
  const tex = useMemo(() => glowTex(), []);
  const burst = (x, y, z, n, color, speed) => {
    for (let k = 0; k < n; k++) {
      const i = sim.next; sim.next = (sim.next + 1) % FW_MAX;
      const u = sim.R() * 2 - 1, th = sim.R() * Math.PI * 2, r = Math.sqrt(1 - u * u), sp = speed * (0.7 + sim.R() * 0.3);
      sim.pos.set([x, y, z], i * 3);
      sim.vel.set([Math.cos(th) * r * sp, u * sp, Math.sin(th) * r * sp], i * 3);
      sim.col.set([color.r, color.g, color.b], i * 3);
      sim.life[i] = 1.4 + sim.R() * 0.6;
    }
  };
  // shells go up from the stadium's rim, over the crowd, and burst above it
  const launch = () => {
    const a = sim.R() * Math.PI * 2, r = 50 + sim.R() * 36;
    sim.shells.push({ x: Math.sin(a) * r, z: Math.cos(a) * r, y: 4, vy: 22 + sim.R() * 6, fuse: 1.1 + sim.R() * 0.5,
      color: FW_COLORS[Math.floor(sim.R() * FW_COLORS.length)] });
  };
  useFrame((_, dtRaw) => {
    const dt = Math.min(dtRaw || 0.016, 0.05), c = cue.current;
    if (on) {
      // the welcome gets a volley; the final count goes up every second
      if (c.beat !== sim.lastBeat && (c.beat === "welcome" || c.beat === "go")) for (let k = 0; k < (c.beat === "go" ? 8 : 4); k++) launch();
      sim.lastBeat = c.beat;
      sim.timer -= dt;
      if (sim.timer <= 0) { launch(); sim.timer = c.final ? 0.35 : 4 + sim.R() * 3; }
    }
    for (let s = sim.shells.length - 1; s >= 0; s--) {
      const sh = sim.shells[s];
      sh.y += sh.vy * dt; sh.vy -= 9 * dt; sh.fuse -= dt;
      if (sh.fuse <= 0) { burst(sh.x, sh.y, sh.z, 160, sh.color, 11); sim.shells.splice(s, 1); }
      else burst(sh.x, sh.y, sh.z, 1, sh.color, 0.3);   // the climbing trail
    }
    for (let i = 0; i < FW_MAX; i++) {
      if (sim.life[i] <= 0) { sim.pos[i * 3 + 1] = -999; continue; }
      sim.life[i] -= dt;
      sim.vel[i * 3 + 1] -= 3.2 * dt;
      const drag = 1 - 1.6 * dt;
      sim.vel[i * 3] *= drag; sim.vel[i * 3 + 1] *= drag; sim.vel[i * 3 + 2] *= drag;
      sim.pos[i * 3] += sim.vel[i * 3] * dt; sim.pos[i * 3 + 1] += sim.vel[i * 3 + 1] * dt; sim.pos[i * 3 + 2] += sim.vel[i * 3 + 2] * dt;
    }
    const geo = pts.current && pts.current.geometry; if (!geo) return;
    geo.attributes.position.needsUpdate = true; geo.attributes.color.needsUpdate = true;
  });
  return (
    <points ref={pts} frustumCulled={false}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[sim.pos, 3]} />
        <bufferAttribute attach="attributes-color" args={[sim.col, 3]} />
      </bufferGeometry>
      <pointsMaterial map={tex} size={1.1} vertexColors transparent depthWrite={false} blending={ADD} toneMapped={false} fog={false} />
    </points>
  );
}

// ── THE CAMERA — a slow ringside drift; drag to look around ──
function RingCamera({ cue }) {
  const { camera, gl } = useThree();
  // `home` is where YOU left the camera; the idle drift only sways ±~11° around
  // it (an earlier version added the sway up and wandered ~40° off the ring)
  const S = useRef({ yaw: 0, home: 0, pitch: 0, drag: null, idle: 0, fx: 0 });
  useLayoutEffect(() => {
    if (camera.fov !== 50) { camera.fov = 50; camera.updateProjectionMatrix(); }   // back from the Sky Box (its camera breathes its own field of view)
    const el = gl.domElement, s = S.current;
    const down = (e) => { s.drag = { x: e.clientX, y: e.clientY, yaw: s.yaw, pitch: s.pitch }; s.idle = 0; };
    const move = (e) => { if (!s.drag) return; s.yaw = s.home = s.drag.yaw - (e.clientX - s.drag.x) * 0.006; s.pitch = THREE.MathUtils.clamp(s.drag.pitch + (e.clientY - s.drag.y) * 0.004, -0.25, 0.5); };
    const up = () => { s.drag = null; };
    el.addEventListener("pointerdown", down); window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
    return () => { el.removeEventListener("pointerdown", down); window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
  }, [gl]);
  useFrame(({ clock }, dtRaw) => {
    const dt = Math.min(dtRaw || 0.016, 0.05), s = S.current, c = cue.current, t = clock.elapsedTime;
    if (!s.drag) {   // a gentle sway once you let go, eased in so it never jumps
      s.idle += dt;
      const sway = Math.min(1, Math.max(0, (s.idle - 4) / 3)) * Math.sin(s.idle * 0.12) * 0.2;
      s.yaw += (s.home + sway - s.yaw) * Math.min(1, dt * 2);
    }
    const want = c.corner ? CORNERS[c.corner] * 0.45 : 0;
    s.fx += (want - s.fx) * Math.min(1, dt * 1.5);
    const r = c.final ? 10.2 : 11.4, h = 3.6 + s.pitch * 6;
    camera.position.set(Math.sin(s.yaw) * r, h, Math.cos(s.yaw) * r);
    camera.lookAt(s.fx, 1.3, 0);
  });
  return null;
}

function RingsideImpl({ director, cue, teams, fireworks = true, arena = "shard", sky = false, userCam = null }) {
  return (
    <Canvas
      dpr={[1, 1.5]}
      gl={{ antialias: true, powerPreference: "high-performance" }}
      camera={{ fov: 50, near: 0.1, far: 3.4 * WORLD_BOUNDS.radius * 1.6, position: [0, 3.6, 11.4] }}
      onCreated={({ gl }) => { gl.toneMapping = THREE.ACESFilmicToneMapping; gl.toneMappingExposure = 1.05; }}
    >
      <color attach="background" args={["#07040d"]} />
      {/* fog only starts past the top ring: the islands stay crisp, the world beyond keeps its own (fog-free) light */}
      <fog attach="fog" args={["#120a22", 110, 300]} />
      <Lights />
      <Arena bounds={WORLD_BOUNDS} variant={arena} />
      <Stands cue={cue} director={director} teams={teams} />
      {TETHERS && <Tethers />}
      {/* THE LINE-UP FACES YOU (caP'n 2026-10-21: "have the shatterena face us and be able to see Willie's dialogue"):
          Willie + both corners face local +z. From the Sky Box (out at SKY_ANGLE) that was a back/side view, so in the
          box the whole stage turns to face it. The plate, stands and world stay put. */}
      <group rotation={[0, sky ? SKY_ANGLE : 0, 0]}>
        <Corner which="A" side={0} team={teams[0]} cue={cue} />
        <Corner which="B" side={1} team={teams[1]} cue={cue} />
        <Dais cue={cue} />
      </group>
      <Fireworks cue={cue} on={fireworks} />
      <Director director={director} cue={cue} />
      {userCam && <SkyBox userCam={userCam} />}   {/* your body is in the box whichever camera you use */}
      {sky && userCam ? <SkyOnlyCamera userCam={userCam} cue={cue} /> : <RingCamera cue={cue} />}
    </Canvas>
  );
}

// teams only change when the pairing changes; everything else rides refs
export default memo(RingsideImpl, (a, b) => a.fireworks === b.fireworks && String(a.teams) === String(b.teams) && a.sky === b.sky && a.arena === b.arena);
